import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { PositionDetail } from "../../api/structure";
import { layoutsApi, type LayoutView, type PlacementView } from "../../api/layouts";
import { PlacementPreview } from "../../components/layout/PlacementPreview";
import Ic from "../../components/icons.jsx";

/**
 * Layout section of the position card (sprint 6 §5): where the position is marked, its own
 * layouts and "Novi layout". Rendered only with `layouts:view` (the page decides).
 */
export function PositionLayoutsSection({
  detail, canCreateLayout, onNewLayout,
}: {
  detail: PositionDetail;
  canCreateLayout: boolean;
  onNewLayout: () => void;
}) {
  const { t } = useTranslation();
  const [placements, setPlacements] = useState<PlacementView[]>([]);
  const [ownLayouts, setOwnLayouts] = useState<LayoutView[]>([]);
  const { id: detailId, placementCount, layoutCount } = detail;

  useEffect(() => {
    if (placementCount === 0) { setPlacements([]); return; }
    let cancelled = false;
    layoutsApi.placements(detailId)
      .then((list) => { if (!cancelled) setPlacements(list); })
      .catch(() => { if (!cancelled) setPlacements([]); });
    return () => { cancelled = true; };
  }, [detailId, placementCount]);
  useEffect(() => {
    if (layoutCount === 0) { setOwnLayouts([]); return; }
    let cancelled = false;
    layoutsApi.forPosition(detailId)
      .then((list) => { if (!cancelled) setOwnLayouts(list); })
      .catch(() => { if (!cancelled) setOwnLayouts([]); });
    return () => { cancelled = true; };
  }, [detailId, layoutCount]);

  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h3>{t("layout.section.title", "Layout")}</h3>
          <div className="sub">{t("layout.section.sub", "Gde je pozicija na slici nad-entiteta i sopstveni layouti.")}</div>
        </div>
        {canCreateLayout && (
          <button type="button" className="btn sm" onClick={onNewLayout}>
            <Ic.plus /> {t("layout.new.title", "Novi layout")}
          </button>
        )}
      </div>
      {placements.length === 0 && ownLayouts.length === 0 ? (
        <div className="empty">
          {placementCount > 0 || layoutCount > 0
            ? t("common.loading", "Učitavanje…")
            : t("layout.section.empty", "Pozicija nije ni na jednom layoutu i nema sopstveni layout.")}
        </div>
      ) : (
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {placements.length > 0 && <PlacementPreview placements={placements} positionId={detail.id} />}
          {ownLayouts.length > 0 && (
            <div>
              <div className="marker-card-label" style={{ marginBottom: 6 }}>{t("layout.section.own", "Layouti ove pozicije")}</div>
              <div className="layout-thumbs">
                {ownLayouts.map((l) => (
                  <Link key={l.id} to={`/layout/${l.id}`} className="layout-thumb" title={l.name}>
                    {l.kind === "MODEL"
                      ? <span className="layout-thumb-model" title={t("layout.kind.model", "3D model")}><Ic.box aria-hidden="true" /><b>{t("layout.kind.badge", "3D")}</b></span>
                      : <img src={l.thumbnailUrl ?? undefined} alt="" loading="lazy" />}
                    <span>{l.name}</span>
                    <small>{t("layout.markerCount", "{{count}} markera", { count: l.markerCount })}</small>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
